import { useEffect, useState } from "react";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import LoadingButton from "../../Components/LoadingButton";
import { useFullScreen } from "../../utils/dialog";

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: { reason: string; comment: string }) => void;
  loading: boolean;
  requestID: string;
};

export default function EscalateDialog({ open, onClose, onSubmit, loading, requestID }: Props) {
  const { fullScreen } = useFullScreen();
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open && loading) {
      return () => {
        onClose();
        setComment("");
        setReason("");
      };
    }
  }, [open, onClose, loading]);

  const handleSubmit = () => {
    onSubmit({ reason, comment });
  };

  return (
    <Dialog open={open} fullScreen={fullScreen} aria-labelledby="form-dialog-escalate">
      <DialogTitle id="form-dialog-title">{`Escalate request #${requestID}`}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>Please fill in the reason for escalation</DialogContentText>

        <Grid
          container
          spacing={2}
          sx={{
            mt: 0,
          }}
        >
          <Grid size={12}>
            <TextField
              id="reason"
              name="reason"
              label="Escalation Reason"
              variant="outlined"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              select
              helperText="Please select the reason for escalation"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            >
              <MenuItem value="time">Long resolution time</MenuItem>
              <MenuItem value="business">Business Impact</MenuItem>
              <MenuItem value="technical">Technical expertise</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Grid>
          <Grid size={12}>
            <TextField
              id="comment"
              name="comment"
              label="Add Comment"
              variant="outlined"
              value={comment}
              onChange={(event) => {
                setComment(event.target.value);
              }}
              multiline
              rows={3}
              maxRows={3}
              helperText="Optional"
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button color="primary" disabled={loading} onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          color="primary"
          size="medium"
          variant="contained"
          onClick={handleSubmit}
          disabled={!reason || loading}
          loading={loading}
          mixpanelEventId="support.escalate.submit"
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
